import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCanManageCurrentOrg, useCanReadCurrentPatient, useGetCurrentOrg, useGetCurrentPatientId } from 'src/hooks/abilityHooks';
import { OrganizationProvider } from 'src/contexts/OrganizationContext';
import Loading from './Loading';
import { useConnectionStatusPortal } from 'src/hooks/useConnectionStatusPortal';
import { getLoginRedirectFromPath } from 'src/utils/url';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOrg?: boolean;
  requireOrgAdmin?: boolean;
}

export const ProtectedRoute = ({ children, requireOrg = true, requireOrgAdmin = false }: ProtectedRouteProps) => {
  const {
    isLoading,
    isAuthenticated,
    isDemoSession,
    isLoggingOut,
    logout,
    demoEpicPatientId,
    demoEpicOrgId,
  } = useAuth();
  const pathname = window.location.pathname;
  const organization = useGetCurrentOrg();
  const patientId = useGetCurrentPatientId();
  const canReadCurrentPatient = useCanReadCurrentPatient();
  const canManageOrg = useCanManageCurrentOrg();
  const { updateConnectionStatus } = useConnectionStatusPortal();

  useEffect(() => {
    if (organization) {
      updateConnectionStatus(organization.id);
    }
  }, [ organization, updateConnectionStatus ]);

  useEffect(() => {
    // logs user out of demo session if not in a valid url
    if (!isDemoSession) return;

    const demoPaths = [
      `/dashboard/demo`,
      `/${demoEpicOrgId}/dashboard/${demoEpicPatientId}`,
    ];

    if (!isLoggingOut && !demoPaths.includes(pathname)) {
      logout();
    }
  }, [isDemoSession, logout, pathname, demoEpicOrgId, demoEpicPatientId, isLoggingOut]);

  useEffect(() => {
    if (isLoading) return;

    // Temp bandaid fix to routes
    if (!isDemoSession && pathname === `/${demoEpicOrgId}/dashboard/${demoEpicPatientId}`) {
      window.location.href = '/dashboard/demo';
      return;
    }

    if (requireOrg && !organization) {
      window.location.href = getLoginRedirectFromPath(pathname);
      return;
    }

    if (!isAuthenticated && !isLoggingOut) {
      window.location.href = getLoginRedirectFromPath(pathname);
      return;
    }

    if (requireOrgAdmin && !canManageOrg) {
      window.location.href = '/error';
      return;
    }

    if (isAuthenticated && patientId && !canReadCurrentPatient) {
      window.location.href = '/error';
      return;
    }
  }, [requireOrg, organization, isLoading, isAuthenticated, isLoggingOut, requireOrgAdmin, canManageOrg, canReadCurrentPatient, patientId, isDemoSession, pathname, demoEpicOrgId, demoEpicPatientId]);

  if (isLoading) {
    return <Loading className="pt-4" message="Loading authentication..." />;
  }

  if (requireOrg && !organization) {
    return null;
  }

  if (requireOrg && organization) {
    return (
      <OrganizationProvider
        organization={organization}
        patientId={patientId}
        patientData={null}
        loadingPatientData={false}
      >
        {children}
      </OrganizationProvider>
    );
  }

  return <>{children}</>;
}
